import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getCaptures, deleteCaptures } from '../api/inspector';
import { WebhookCaptureResponse } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';

const TRIGGER_BASE =
  (import.meta.env.VITE_TRIGGER_URL ?? '').replace(/\/$/, '')
  || window.location.origin;

function tryPretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function CaptureRow({ capture }: { capture: WebhookCaptureResponse }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3 text-sm space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{new Date(capture.receivedAt).toLocaleString()}</span>
        <span className="font-mono">{capture.id.slice(0, 8)}</span>
      </div>
      <details open>
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Body</summary>
        <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto font-mono">
          {tryPretty(capture.body)}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Headers</summary>
        <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto font-mono">
          {tryPretty(capture.headers)}
        </pre>
      </details>
      {capture.query !== '{}' && (
        <details>
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Query</summary>
          <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto font-mono">
            {tryPretty(capture.query)}
          </pre>
        </details>
      )}
    </div>
  );
}

export function InspectorPage() {
  const [token, setToken] = useState(() => crypto.randomUUID());
  const qc = useQueryClient();

  const captureUrl = `${TRIGGER_BASE}/capture/${token}`;

  const { data: captures = [], isFetching } = useQuery({
    queryKey: ['inspector', token],
    queryFn: () => getCaptures(token),
    refetchInterval: 3000,
  });

  const clearMutation = useMutation({
    mutationFn: () => deleteCaptures(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inspector', token] }),
  });

  function newSession() {
    setToken(crypto.randomUUID());
  }

  function copyUrl() {
    navigator.clipboard.writeText(captureUrl);
    toast.success('URL copied');
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <PageHeader
        title="Webhook inspector"
        description="Capture test requests to see exactly what a sender posts."
      />

      <Card>
        <CardContent className="pt-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Send a <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">POST</code> request to the URL below.
            Any JSON body is captured and shown here. Polling every 3 s.
          </p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 bg-muted rounded-md px-3 py-1.5 text-sm font-mono break-all">
              {captureUrl}
            </code>
            <Button variant="outline" size="sm" className="shrink-0" onClick={copyUrl}>
              <Copy className="h-4 w-4" />
              Copy
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="xs"
              onClick={() => qc.invalidateQueries({ queryKey: ['inspector', token] })}
              disabled={isFetching}
            >
              <RefreshCw className={isFetching ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button
              variant="outline"
              size="xs"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/40"
              onClick={() => clearMutation.mutate()}
              disabled={clearMutation.isPending || captures.length === 0}
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </Button>
            <Button variant="outline" size="xs" className="ml-auto" onClick={newSession}>
              <RotateCcw className="h-3 w-3" />
              New URL
            </Button>
          </div>
        </CardContent>
      </Card>

      {captures.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No requests captured yet. Send a POST to the URL above.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{captures.length} request(s), newest first</p>
          {captures.map(c => <CaptureRow key={c.id} capture={c} />)}
        </div>
      )}
    </div>
  );
}
